import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  Anchor,
  Badge,
  Flex,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Text,
} from '@percent/lemonade'
import { filterDomains } from '../../../utils/filterDomains'
import { groupDomainsByArea } from '../../../utils/groupDomainsByArea'
import type { DomainsTableProps } from './DomainsTable.types'

export function DomainsTable({
  domains,
  isLoading,
  getDomainHref,
}: DomainsTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')

  const filteredDomains = useMemo(
    () => filterDomains(domains ?? [], searchValue),
    [domains, searchValue]
  )

  // Filter before grouping so an area with no matching domain loses its header too
  const domainAreas = useMemo(
    () => groupDomainsByArea(filteredDomains),
    [filteredDomains]
  )

  const columns = [
    {
      id: 'name',
      content: t('rolesAndPermissions.table.name'),
      props: { width: '25%' },
    },
    {
      id: 'description',
      content: t('rolesAndPermissions.table.description'),
      props: { width: '40%' },
    },
    {
      id: 'roles',
      content: t('rolesAndPermissions.table.roles'),
      props: { width: '35%' },
    },
  ]

  return (
    <TableProvider
      columns={columns}
      totalResults={filteredDomains.length}
      isLoading={isLoading}
      disableHorizontalScroll
      illustration="no-results"
      emptyTableTitle={t('rolesAndPermissions.domains.emptyTable.title')}
      emptyTableDescription={t(
        'rolesAndPermissions.domains.emptyTable.description'
      )}
    >
      <TableSearchAndFilters>
        <SearchInput
          width="100%"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          handleClearValue={() => setSearchValue('')}
          placeholder={t('rolesAndPermissions.domains.searchPlaceholder')}
          inputSize="small"
          data-testid="domains-search-input"
        />
      </TableSearchAndFilters>
      <Table>
        <TableBody>
          {domainAreas.flatMap(({ area, domains: areaDomains }) => [
            <TableRow key={`area-${area}`} data-testid={`domain-area-${area}`}>
              <TableCell colSpan={columns.length}>
                <Text size="small" fontWeight="semiBold">
                  {area}
                </Text>
              </TableCell>
            </TableRow>,
            ...areaDomains.map((domain) => (
              <TableRow
                key={domain.id}
                data-testid={`domain-row-${domain.id}`}
                onClick={() => navigate(getDomainHref(domain))}
              >
                <TableCell>
                  <Anchor
                    component={Link}
                    to={getDomainHref(domain)}
                    // The row's own click handler would navigate a second time
                    onClick={(event) => event.stopPropagation()}
                    data-testid={`domain-name-${domain.id}`}
                  >
                    {domain.name}
                  </Anchor>
                </TableCell>
                <TableCell>{domain.description}</TableCell>
                <TableCell>
                  <Flex direction="row" gap="xs" wrap="wrap">
                    {domain.roles.map((role) => (
                      <Badge key={role.id} variant="default">
                        {role.name}
                      </Badge>
                    ))}
                  </Flex>
                </TableCell>
              </TableRow>
            )),
          ])}
        </TableBody>
      </Table>
    </TableProvider>
  )
}
